import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { ArticleIndex } from '@vs-shop/schemas/branded/ContentSchema';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/blog/articles';
const HTTP_METHOD = 'get';

const ListArticlesError = createBackendApiError({
  domain: 'Article',
  endpoint: 'List',
  path: ENDPOINT,
  identifierField: 'page',
} as const);

export type ArticleListError = BackendApiErrorUnion<typeof ListArticlesError>;

const listArticlesFetch = (
  page: number,
  itemsPerPage: number,
  section?: string,
  categoryId?: string,
  authorId?: number
) => {
  return Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({
        query: {
          page,
          itemsPerPage,
          ...(section !== undefined ? { 'section.translations.slug': section } : {}),
          ...(categoryId !== undefined ? { 'categories.id': categoryId } : {}),
          ...(authorId !== undefined ? { 'authors.id': authorId } : {}),
        },
      });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'articles.page': page,
        'articles.itemsPerPage': itemsPerPage,
        ...(section !== undefined ? { 'articles.section': section } : {}),
        ...(categoryId !== undefined ? { 'articles.categoryId': categoryId } : {}),
        ...(authorId !== undefined ? { 'articles.authorId': authorId } : {}),
      },
    })
  );
};

export const listArticles = (
  page: number,
  itemsPerPage: number,
  section?: string,
  categoryId?: string,
  authorId?: number
) =>
  listArticlesFetch(page, itemsPerPage, section, categoryId, authorId).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.withSpan('ValidateResponse'),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': (response) => schemaBodyJsonClean(HydraCollectionResponse(ArticleIndex))(response),
        404: (response) => Effect.fail(new ListArticlesError.NotFoundError({ page, response })),
        orElse: (response) =>
          Effect.fail(new ListArticlesError.ApiRequestError({ page, response })),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.ArticleList,
        'Debug',
        `Successfully resolved ${String(result['hydra:member'].length)} articles, page ${String(page)}${section !== undefined ? `, section ${section}` : ''}${categoryId !== undefined ? `, categoryId ${categoryId}` : ''}${authorId !== undefined ? `, authorId ${String(authorId)}` : ''}`
      )
    ),
    Effect.withSpan('api.article.list', {
      kind: 'internal',
      attributes: {
        'articles.page': page,
        'articles.itemsPerPage': itemsPerPage,
        ...(section !== undefined ? { 'articles.section': section } : {}),
        ...(categoryId !== undefined ? { 'articles.categoryId': categoryId } : {}),
        ...(authorId !== undefined ? { 'articles.authorId': authorId } : {}),
      },
    })
  );
